import { useWorkOrderGanttMenu } from "@/pages/workOrders/hooks/useWorkOrderGanttMenu";
import { type MenuOptionType, useContextMenuMESF } from "@dexteel/mesf-core";
import { Add, DeleteOutline, Edit, Tune } from "@mui/icons-material";
import { Box } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CalendarEvent } from "@/shared/repositories/CalendarEventRepository";
import type { CalendarItem } from "../components/EventBar";

const DAY_MENU_ID = "calendarDayMenu";
const ITEM_MENU_ID = "calendarItemMenu";
// bold = left-click action
const BOLD = { fontWeight: 700 };

type Handlers = {
	onNewEvent: (date: Date) => void;
	onDayConfig: (date: Date) => void;
	onEditItem: (item: CalendarItem) => void;
	onDelete: (event: CalendarEvent) => void;
};

export const useCalendarMenus = (handlers: Handlers) => {
	const { show, showContextMenu, registerConfig, onClickOutside } =
		useContextMenuMESF();
	const { getOptions: getWoOptions, menu: woMenu } = useWorkOrderGanttMenu();
	// bar keeps hover look while its menu is open
	const [targetKey, setTargetKey] = useState<string | null>(null);

	// registered once; handlers read at open time
	const handlersRef = useRef(handlers);
	useEffect(() => {
		handlersRef.current = handlers;
	});

	useEffect(() => {
		registerConfig({
			id: DAY_MENU_ID,
			getOptions: (date: Date): MenuOptionType[] => [
				{
					name: "New Event",
					key: "new_event",
					icon: <Add />,
					style: BOLD,
					onClick: () => handlersRef.current.onNewEvent(date),
				},
				{
					name: "Day Config…",
					key: "day_config",
					icon: <Tune />,
					onClick: () => handlersRef.current.onDayConfig(date),
				},
			],
		});
		registerConfig({
			id: ITEM_MENU_ID,
			getOptions: (item: CalendarItem): MenuOptionType[] => {
				const isWo = item.kind === "wo";
				const edit: MenuOptionType = {
					name: isWo ? "Edit Work Order" : "Edit Event",
					key: "edit",
					icon: <Edit />,
					style: BOLD,
					onClick: () => handlersRef.current.onEditItem(item),
				};
				const del: MenuOptionType = {
					name: isWo ? "Delete Work Order" : "Delete Event",
					key: "delete",
					icon: <DeleteOutline />,
					onClick: () => handlersRef.current.onDelete(item.event),
				};
				if (!isWo) return [edit, del];

				const { wo } = item;
				const options = [
					edit,
					...getWoOptions({
						WorkOrderId: wo.WorkOrderId,
						AssetId: wo.AssetId,
						ShiftId: wo.DisplayShiftId,
						Status: wo.Status,
					}).filter(
						(o) =>
							o.key !== "edit_times" ||
							wo.Status === "Running" ||
							wo.Status === "Completed",
					),
				];
				// before See in Manager (last)
				if (wo.Status === "Pending") options.splice(-1, 0, del);
				return options;
			},
		});
	}, [registerConfig, getWoOptions]);

	const onDayContextMenu = useCallback(
		(e: React.MouseEvent, date: Date) => {
			setTargetKey(null);
			showContextMenu(e, date, DAY_MENU_ID);
		},
		[showContextMenu],
	);
	const onItemContextMenu = useCallback(
		(e: React.MouseEvent, item: CalendarItem) => {
			setTargetKey(item.key);
			showContextMenu(e, item, ITEM_MENU_ID);
		},
		[showContextMenu],
	);

	// house menu has no backdrop: layer eats the dismissing click;
	// right-click passes through so the menu retargets
	const shield = show && (
		<Box
			sx={{ position: "fixed", inset: 0, zIndex: (t) => t.zIndex.modal }}
			onContextMenu={(e) => {
				e.preventDefault();
				onClickOutside();
				// [0] is this layer
				document.elementsFromPoint(e.clientX, e.clientY)[1]?.dispatchEvent(
					new MouseEvent("contextmenu", {
						bubbles: true,
						cancelable: true,
						clientX: e.clientX,
						clientY: e.clientY,
					}),
				);
			}}
		/>
	);

	return {
		menuTargetKey: show ? targetKey : null,
		onDayContextMenu,
		onItemContextMenu,
		menus: (
			<>
				{shield}
				{woMenu}
			</>
		),
	};
};
